import { useEffect, useRef, useState } from 'react'
import { message } from 'antd'
import moment from 'moment'

import { useAppState } from '../state'

import { TenantRef } from '../models/tenant'
import { SwtchError } from '../models/error'
import { V3Transaction, V3TransactionState } from '../models/v3-transaction'
import { V3TransactionFilter } from '../services/data-provider/v3-transaction'
import { PaginationMeta } from '../models/pagination'

import { GetV3Transactions, GetV3TransactionsStats, BulkSettleV3Transactions } from '../services/data-provider/v3-transaction'
import { TransactionMetric } from '../models/analytics'
import { isValidEmail } from '../helpers/regularExpression/email'
import { testIfInputIsPositiveNumber } from 'helpers/regularExpression/input'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'

const initialPagination = { currentPage: 1, perPage: 10, totalEntries: 0, totalPages: 0 }

export const useV3Transactions = () => {
  const { selectedTenant, isMockUpEnabled, currentUser } = useAppState()
  const location = useLocation()
  const { transaction_id, start_date, end_date } = queryString.parse(location.search)
  const [startDate, setStartDate] = useState(start_date ? moment(start_date) : moment().subtract(6, 'days'))
  const [endDate, setEndDate] = useState(end_date ? moment(end_date) : moment().endOf('day'))
  const [tenants, setTenants] = useState<TenantRef[]>([])
  const [pagination, setPagination] = useState<PaginationMeta>(initialPagination)
  const [metrics, setMetrics] = useState<TransactionMetric>()

  const [metricsLoading, setMetricsLoading] = useState(false)
  const [transactions, setTransactions] = useState<V3Transaction[]>([])
  const [exportToken, setExportToken] = useState<string>()

  const [loading, setLoading] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [showSearchQuery, setShowSearchQuery] = useState(false)
  const [error, setError] = useState<SwtchError>()
  const [reset, setReset] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([])
  const [bulkSettleLoading, setBulkSettleLoading] = useState(false)
  const [appliedPaymentState, setAppliedPaymentState] = useState<string | undefined>(undefined)
  const [filter, setFilter] = useState<V3TransactionFilter>({
    page: 1,
    defaultTenant: selectedTenant,
    startMethod: undefined,
    state: undefined,
    paymentState: undefined,
    transactionId: transaction_id as string | undefined,
    userName: undefined,
    masterAccounts: [],
  })

  useEffect(() => {
    if (reset) {
      setTenants([])
      setReset(false)
    }
  }, [reset])

  const isInitialMount = useRef(true)

  useEffect(() => {
    fetchTransactions()
    if (isInitialMount.current) {
      fetchTransactionStats()
      isInitialMount.current = false
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [filter.page, pagination.currentPage])

  const reloadTransactions = () => {
    fetchTransactions()
    fetchTransactionStats()
  }

  const fetchTransactionsAndStats = () => {
    const isValid = checkValidEmail(filter.email)
    if (!isValid) {
      return
    }

    setSelectedRowKeys([])
    setAppliedPaymentState(filter.paymentState)
    const filterWithPage1 = { ...filter, page: 1 }
    setFilter(filterWithPage1)
    setPagination((prev) => ({ ...prev, currentPage: 1 }))
    fetchTransactions(filterWithPage1)
    fetchTransactionStats(filterWithPage1)
  }

  const fetchDataWithQuery = () => {
    setSelectedRowKeys([])
    setAppliedPaymentState(filter.paymentState)
    setShowSearchQuery(true)
    const filterWithPage1 = { ...filter, page: 1 }
    setFilter(filterWithPage1)
    setPagination((prev) => ({ ...prev, currentPage: 1 }))
    fetchTransactions(filterWithPage1)
    fetchTransactionStats(filterWithPage1)
  }

  const fetchTransactions = (overrideFilter?: V3TransactionFilter) => {
    const filterToUse = overrideFilter ?? filter
    setLoading(true)
    setError(undefined)
    GetV3Transactions(currentUser, isMockUpEnabled, { ...filterToUse, skipTotalCountAndExportToken: true }, startDate, endDate)
      .then((resp) => {
        setTransactions(resp.data)
        setPagination((prev) => ({
          ...resp.pagination,
          totalEntries: prev.totalEntries,
        }))
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false))
  }

  const fetchTransactionStats = (overrideFilter?: V3TransactionFilter) => {
    const filterToUse = overrideFilter ?? filter
    setError(undefined)
    setMetricsLoading(true)
    GetV3TransactionsStats(isMockUpEnabled, filterToUse, startDate, endDate)
      .then((metric) => {
        setMetrics(metric)
        setPagination((prev) => ({ ...prev, totalEntries: metric.count }))
      })
      .catch((err) => {
        setError((prevState) => {
          if (prevState) {
            let newError = prevState
            newError.messages = prevState.messages || []
            newError.messages.push(...err.messages)
            return newError
          }
          return err
        })
      })
      .finally(() => setMetricsLoading(false))
  }

  const handleFilterChange = (filter: V3TransactionFilter) => {
    setFilter((prevFilters) => ({ ...prevFilters, ...filter }))
  }

  const handlePaginationChange = (page: number, pageSize?: number) => {
    setSelectedRowKeys([])
    setPagination({ ...pagination, currentPage: page })
    setFilter((prev) => ({ ...prev, page }))
    return
  }

  const handleTenantSelection = (tenants: TenantRef[]) =>
    setFilter((prev) => ({
      ...prev,
      tenants: tenants.filter((obj, index) => {
        return index === tenants.findIndex((o) => obj.id === o.id)
      }),
    }))

  const handleEmail = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFilter((prev) => ({ ...prev, email: e.target.value }))

  const checkValidEmail = (email?: string) => {
    message.destroy()
    if (!email) {
      setDisabled(false)
      return true
    }
    if (isValidEmail(email.trim())) {
      setDisabled(false)
      return true
    } else {
      setDisabled(true)
      message.error('Invalid email')
      return false
    }
  }

  const handleTransactionId = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value: inputValue } = e.target
    if (testIfInputIsPositiveNumber(inputValue) || inputValue === '') {
      setFilter((prev) => ({ ...prev, transactionId: inputValue }))
    }
  }

  const handleOcppTransactionId = (value: string) => {
    setFilter((prev) => ({
      ...prev,
      ocppTransactionId: value || undefined,
    }))
  }

  const handleChangeDate = (dates: moment.Moment[]) => {
    if (dates[0] !== startDate) {
      setStartDate(dates[0])
    }
    if (dates[1] !== endDate) {
      setEndDate(dates[1])
    }
  }

  const handleStartmethod = (startMethod: string) => setFilter((prev) => ({ ...prev, startMethod }))

  const handleState = (state: V3TransactionState) => setFilter((prev) => ({ ...prev, state }))

  const handlePaymentState = (paymentState: string | undefined) => setFilter((prev) => ({ ...prev, paymentState }))

  const handleResetAdvancedSearch = () => {
    setDisabled(false)
    setReset(true)
    setSelectedRowKeys([])
    setAppliedPaymentState(undefined)
    setFilter((prev) => ({
      ...prev,
      startMethod: undefined,
      state: undefined,
      paymentState: undefined,
      userName: undefined,
      chargers: [],
      listingTitle: [],
      email: undefined,
      transactionId: undefined,
      tenants: [],
      ocppTransactionId: undefined,
    }))
  }

  const handleUsername = (e: string) => {
    setFilter((prev) => ({ ...prev, userName: e }))
  }

  const handleChargerId = (chargers: string[]) => {
    setFilter((prev) => ({ ...prev, chargers }))
  }

  const isBulkSettleVisible = appliedPaymentState === 'invoiced' && currentUser?.role === 'admin'

  const handleRowSelect = (record: V3Transaction, selected: boolean) => {
    setSelectedRowKeys((prev) =>
      selected ? [...prev, record.id] : prev.filter((key) => key !== record.id),
    )
  }

  const handleSelectAllOnPage = (selected: boolean) => {
    if (selected) {
      setSelectedRowKeys((prev) => {
        const currentPageIds = transactions.map((t) => t.id)
        const merged = [...prev, ...currentPageIds]
        return merged.filter((id, index) => merged.indexOf(id) === index)
      })
    } else {
      setSelectedRowKeys([])
    }
  }

  const bulkSettle = () => {
    if (selectedRowKeys.length === 0) return
    setBulkSettleLoading(true)
    BulkSettleV3Transactions(selectedRowKeys)
      .then((resp) => {
        if (resp.success) {
          message.success(resp.msg)
        } else {
          message.error(resp.msg)
        }
        setSelectedRowKeys([])
        reloadTransactions()
      })
      .catch((err) => {
        message.error(err?.message || 'Failed to settle transactions')
      })
      .finally(() => setBulkSettleLoading(false))
  }

  return {
    handlePaginationChange,
    handleTenantSelection,
    handleChangeDate,
    handleStartmethod,
    handleState,
    handlePaymentState,
    handleUsername,
    handleChargerId,
    handleResetAdvancedSearch,
    handleEmail,
    handleTransactionId,
    handleOcppTransactionId,
    fetchTransactionsAndStats,
    setTenants,
    setFilter,
    setDisabled,
    checkValidEmail,
    fetchDataWithQuery,
    setReset,
    handleFilterChange,
    reloadTransactions,
    handleRowSelect,
    handleSelectAllOnPage,
    bulkSettle,
    isBulkSettleVisible,
    selectedRowKeys,
    bulkSettleLoading,
    reset,
    showSearchQuery,
    disabled,
    startDate,
    endDate,
    metrics,
    metricsLoading,
    exportToken,
    filter,
    tenants,
    transactions,
    pagination,
    loading,
    error,
  }
}

